'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/nn-extra'

const LayerShapes = dynamic(() => import('../_components/widgets/NNExtraWidgets').then(m => m.LayerShapes), { ssr: false })

export default function BackpropForLayers() {
  return (
    <Lesson>
      <Step>
        <H>From single numbers to whole layers</H>
        <P>
          Backpropagation one number at a time works, but it’s slow: a real network has millions of weights. The fix is to do
          the same calculus on whole matrices at once. A layer applied to a batch of examples is one matrix multiply:
        </P>
        <MB>{'Y = X\\,W + \\mathbf{b} \\qquad X: B \\times n_{\\text{in}} \\quad W: n_{\\text{in}} \\times n_{\\text{out}} \\quad Y: B \\times n_{\\text{out}}'}</MB>
        <P>
          Going backwards, we receive <M>{'\\partial L / \\partial Y'}</M>, how much the loss cares about each output, and need
          the gradients for <M>W</M>, <M>{'\\mathbf{b}'}</M> and <M>X</M>. The chain rule gives three short formulas, and there’s
          a trick to remembering them: <strong>every gradient has exactly the same shape as the thing it’s for</strong>. Only one
          arrangement of the pieces makes the shapes fit.
        </P>
        <Figure n="7.5" wide caption="Forward: X times W. Backward: each gradient is built from the incoming ∂L/∂Y and one transposed matrix — and comes out the same shape as its variable. Change the sizes and watch the shapes follow.">
          <LayerShapes />
        </Figure>
      </Step>

      <Step gate={['q-shape', 'linear-backward']}>
        <H>The three formulas</H>
        <MB>{'\\frac{\\partial L}{\\partial W} = X^{\\top} \\frac{\\partial L}{\\partial Y} \\qquad \\frac{\\partial L}{\\partial X} = \\frac{\\partial L}{\\partial Y}\\, W^{\\top} \\qquad \\frac{\\partial L}{\\partial \\mathbf{b}} = \\sum_{\\text{rows}} \\frac{\\partial L}{\\partial Y}'}</MB>
        <Quiz
          id="q-shape"
          question="X is 32 × 784 (a batch of images) and W is 784 × 128. What shape is ∂L/∂W?"
          options={[
            { label: '784 × 128 — the same as W', correct: true },
            { label: '32 × 128', why: 'That’s the shape of Y and of ∂L/∂Y. The gradient for W must match W.' },
            { label: '32 × 784', why: 'That’s the shape of X (and of ∂L/∂X).' },
          ]}
          explain="Xᵀ (784 × 32) times ∂L/∂Y (32 × 128) gives 784 × 128. Summing over the 32 rows is how the whole batch contributes to one gradient."
        />
        <Exercise
          id="linear-backward"
          title="linearBackward"
          {...EX.linearBackward}
          hints={[
            <>
              <C>{'mat.mul(mat.transpose(X), dY)'}</C> and <C>{'mat.mul(dY, mat.transpose(W))'}</C>
            </>,
            <>
              db: <C>{'dY[0].map((_, j) => dY.reduce((s, row) => s + row[j], 0))'}</C>
            </>,
          ]}
        />
      </Step>

      <Step gate="relu-backward">
        <H>Through the activation</H>
        <P>
          An activation acts on each number separately, so its backward pass does too: multiply by its slope, entry by entry.
          For ReLU the slope is 1 where the input was positive and 0 elsewhere. The gradient either passes straight through, or
          it’s blocked.
        </P>
        <Exercise id="relu-backward" title="reluBackward" {...EX.reluBackward} hints={[<><C>{'dA.map((row, i) => row.map((g, j) => (Z[i][j] > 0 ? g : 0)))'}</C></>]} />
      </Step>

      <Step gate="xor-matrix">
        <H>A whole network, by hand</H>
        <P>
          Put the pieces together: a two-layer network trained on XOR with nothing but matrix operations. The forward pass is
          written; the backward pass is four short lines, each one of the formulas above.
        </P>
        <Exercise
          id="xor-matrix"
          title="Matrix backprop on XOR"
          {...EX.xorMatrix}
          hints={[
            <>
              <C>{'const dW2 = mat.mul(mat.transpose(A1), dZ2)'}</C> and <C>{'const db2 = colSums(dZ2)'}</C>
            </>,
            <>
              <C>{'const dA1 = mat.mul(dZ2, mat.transpose(W2))'}</C>, then the ReLU rule for <C>dZ1</C>.
            </>,
            <>
              <C>{'W1 = mat.add(W1, mat.scale(dW1, -lr))'}</C>, and <C>{'b1 = b1.map((v, j) => v - lr * db1[j])'}</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          This is exactly what PyTorch does inside <C>loss.backward()</C>, just on a GPU with much bigger matrices. Training a
          large language model is mostly these matrix multiplies, forwards and backwards, trillions of times over.
        </Callout>
      </Step>
    </Lesson>
  )
}
